import React, { ReactNode, useMemo } from 'react'
import { toast, ToastOptions } from 'react-toastify'

import { useTheme } from '@emotion/react'

import { useEventCapture } from '@surglogs-app/analytics'
import { useHandler } from '@surglogs-app/hooks'

import { useToastTranslation } from './services'
import { Toast, TOAST_WIDTH, ToastProps } from './Toast'

// we don't want to pass ReactNode into PostHog events
const getNodeTextContent = (node: ReactNode | string): string =>
  typeof node === 'string' ? node : 'CUSTOM_COMPONENT'

const TOAST_DURATION = 8000

export interface ApiErrorResponseType<T = string> {
  response?: {
    status: number
    statusText: string
    data?: Partial<{
      error_message: string
      error_details: T
      error_code: string
    }>
  }
}

export const useToast = (opts?: ToastOptions) => {
  const { t } = useToastTranslation()
  const theme = useTheme()
  const { captureEvent } = useEventCapture()

  const defaultProps = useMemo(
    () => ({
      style: {
        padding: 0,
        borderRadius: theme.ui.spacings.md,
        width: TOAST_WIDTH,
        maxWidth: '100%',
      },
      autoClose: TOAST_DURATION,
    }),
    [theme.ui.spacings.md],
  )

  const createCloseHandler = useHandler(
    (variant: ToastProps['variant'], title: ReactNode, text: ReactNode) =>
      (reason?: string | boolean) => {
        captureEvent('notification_dismissed', {
          type: variant ?? 'success',
          title: getNodeTextContent(title),
          text: getNodeTextContent(text),
          // reason is undefined when dismissed by timeout
          dismiss_method: reason ? 'manual' : 'timeout',
        })
      },
  )

  const showToast = useHandler((props: Omit<ToastProps, 'ref'>) => {
    const variant = props.variant ?? 'success'
    toast((toastProps) => <Toast {...{ ...props, ...toastProps }} />, {
      onClose: createCloseHandler(variant, props.title, props.text),
      ...defaultProps,
      ...opts,
    })
  })

  const showErrorToast = useHandler(
    ({
      text,
      error,
      ...rest
    }: Omit<ToastProps, 'ref' | 'variant' | 'title'> & {
      error?: unknown
      title?: string | ReactNode
    }) => {
      const typedError = error as ApiErrorResponseType
      const maybeMessage =
        typedError?.response?.data?.error_message ??
        typedError?.response?.data?.error_details

      const message =
        text ||
        (typeof maybeMessage === 'string'
          ? maybeMessage
          : t('defaultFailureMessage.text'))

      const title = rest.title || t('defaultFailureMessage.title')

      toast(
        (toastProps) => (
          <Toast
            {...{
              title,
              text: message,
              variant: 'danger',
              ...rest,
              ...toastProps,
            }}
          />
        ),
        {
          onClose: createCloseHandler('danger', title, message),
          ...defaultProps,
          ...opts,
        },
      )
    },
  )

  return { showToast, showErrorToast }
}
